import { defineStore } from "pinia";
import { computed, ref, watch, type WatchStopHandle } from "vue";

import { setTokenProvider, setTokenRefresher } from "@/api/client";
import type { AuthClient, AuthSession, AuthUser } from "@/auth";
import { createAuthClient } from "@/auth";
import { clearFirstTouch } from "@/lib/attribution";

/**
 * The role half of PRD §6.3's permission matrix, copied for presentation.
 *
 * Claims carry role slugs, not the permission set (§6.4), so a generic route
 * guard still needs this closed mapping to decide whether to offer a surface.
 * Unknown roles and permissions fail closed. This is never an authorization
 * boundary: the API resolves the authoritative matrix from Postgres on every
 * request.
 */
const ROLE_PERMISSIONS: Readonly<Record<string, ReadonlySet<string>>> = {
  learner: new Set([
    "study.use",
    "enrolment.manage",
    "seat.purchase",
    "seat.request_funding",
    "account.delete_self",
  ]),
  payer: new Set(["seat.purchase", "seat.manage", "billing.view"]),
  admin: new Set([
    "admin.catalogue.read",
    "admin.catalogue.write",
    "admin.topic.delete",
    "admin.feedback.read",
    "admin.report.read",
    "admin.report.write",
    "admin.usage.read",
    "admin.ops.debug",
    "admin.user.read",
    "admin.dsar.execute",
    "admin.role.grant",
    "admin.role.revoke",
    "admin.audit.read",
  ]),
};

export const useAuthStore = defineStore("auth", () => {
  const client: AuthClient = createAuthClient();
  const user = ref<AuthUser | null>(null);
  const token = ref<string | null>(null);
  const error = ref<string | null>(null);
  const loading = ref(false);
  // Role slugs asserted by the current ID token (PRD §6.4). Held in one
  // place and always replaced wholesale from a session, never accumulated
  // and never cached beyond the session it came from — a module-level
  // cache is exactly the sign-out leak §11.5.3 calls out.
  const roles = ref<readonly string[]>([]);
  // A sign-up whose login exists but whose account does not yet: true from
  // the moment `register` creates the login until RegisterView's
  // POST /register has answered. Everything that needs the account (route
  // guards, consent, the home page) waits for it, because until then the
  // API refuses every signed-in request with `unregistered_account`.
  const accountPending = ref(false);

  if (client.onSessionChanged) {
    // Firebase: subscribe to the listener so the store stays in sync
    // with login, logout, AND background token refreshes (<1h). The
    // listener fires immediately with the current state, so this also
    // handles initial session restoration from IndexedDB.
    client.onSessionChanged((session) => {
      user.value = session?.user ?? null;
      token.value = session?.token ?? null;
      // Roles arrive with the token, so a background refresh carrying
      // updated claims (a revoked admin) or a sign-out (null session)
      // moves the UI without a reload. `?? []` covers a session
      // deserialised from before roles shipped.
      roles.value = session?.roles ?? [];
    });
  } else {
    // Mock: no listener pattern needed because its tokens are static.
    const existing = client.getCurrentSession();
    if (existing) {
      user.value = existing.user;
      token.value = existing.token;
      roles.value = existing.roles ?? [];
    }
  }

  setTokenProvider(() => token.value);

  // §6.4's revocation recovery. A token below Postgres'
  // `required_authz_version` floor gets a 401: stale, not insufficient. The
  // client force-mints one token and retries once so a claims mirror that has
  // already converged repairs the session transparently. Grant-only lag does
  // not reject the token and therefore does not enter this path.
  //
  // `refreshSession` updates this store through the session listener as a
  // side effect, so the retry and every later call agree. Returning null when
  // nobody is signed in stops `request` retrying a 401 that is simply correct.
  setTokenRefresher(async () => {
    const session = await client.refreshSession?.();
    return session?.token ?? null;
  });

  // Resolves once the client has determined its initial auth state.
  // main.ts awaits this before `app.mount()` so reloads of authenticated
  // users don't briefly render unauthenticated UI while Firebase
  // restores their session from IndexedDB.
  const ready = client.ready?.() ?? Promise.resolve();

  const isAuthenticated = computed(() => token.value !== null);

  /**
   * Presentation-only permission check from the current token's role claims.
   *
   * A method rather than one boolean keeps route metadata permission-shaped:
   * adding a privileged surface cannot invent another `isSomeRole` guard.
   */
  function hasPermission(permission: string): boolean {
    return roles.value.some(
      (role) => ROLE_PERMISSIONS[role]?.has(permission) ?? false,
    );
  }

  /**
   * Observe account changes without treating a token or role refresh as a
   * new identity. Consumers use this boundary for account-scoped state such
   * as consent and analytics attribution.
   */
  function onIdentityChanged(
    callback: (userId: string | null) => void,
  ): WatchStopHandle {
    return watch(
      () => user.value?.id ?? null,
      (userId) => callback(userId),
      { flush: "sync" },
    );
  }

  function setSession(session: AuthSession): void {
    user.value = session.user;
    token.value = session.token;
    roles.value = session.roles ?? [];
    error.value = null;
  }

  async function register(email: string, password: string): Promise<void> {
    loading.value = true;
    error.value = null;
    // Set before the session lands, so nothing reacting to the new
    // identity sees a signed-in user without also seeing it is pending.
    accountPending.value = true;
    try {
      const session = await client.register(email, password);
      setSession(session);
    } catch (e) {
      accountPending.value = false;
      error.value = e instanceof Error ? e.message : "Registration failed";
      throw e;
    } finally {
      loading.value = false;
    }
  }

  /** POST /register has answered, successfully or not. */
  function accountSetupFinished(): void {
    accountPending.value = false;
  }

  async function login(email: string, password: string): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      const session = await client.login(email, password);
      setSession(session);
    } catch (e) {
      error.value = e instanceof Error ? e.message : "Login failed";
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function logout(): Promise<void> {
    await client.signOut();
    user.value = null;
    token.value = null;
    // §11.5.3 — cached privilege must not outlive the session. Firebase
    // also pushes a null session through the listener, but the mock has
    // no listener, so clear here too rather than relying on one path.
    roles.value = [];
    accountPending.value = false;
    // A12 / landing-pages §7 S3 — a browser context is not a person. Any
    // captured campaign dies with the session so the next person to sign
    // up on a shared device does not inherit this one's acquisition.
    clearFirstTouch();
  }

  async function requestPasswordReset(email: string): Promise<void> {
    loading.value = true;
    error.value = null;
    try {
      await client.requestPasswordReset(email);
    } catch (e) {
      error.value = e instanceof Error ? e.message : "Password reset failed";
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function resendVerificationEmail(): Promise<void> {
    if (!client.resendVerificationEmail) {
      // Mock doesn't implement this (its users are always
      // emailVerified=true). Silent no-op keeps call sites simple.
      return;
    }
    loading.value = true;
    error.value = null;
    try {
      await client.resendVerificationEmail();
    } catch (e) {
      error.value =
        e instanceof Error ? e.message : "Could not resend verification email";
      throw e;
    } finally {
      loading.value = false;
    }
  }

  const isEmailVerified = computed(() => user.value?.emailVerified ?? false);

  // Force-refresh the session so the API token's email_verified claim
  // matches the live verification state (see AuthClient.refreshSession).
  // Returns the reconciled emailVerified value. Used by the subscribe
  // flow — the cached token can otherwise 403 right after the user
  // verifies their email, even though the UI shows the Subscribe CTA.
  async function refreshSession(): Promise<boolean> {
    if (!client.refreshSession) return isEmailVerified.value;
    const session = await client.refreshSession();
    // Firebase pushes the update through onSessionChanged too, but the
    // mock has no listener — update here so both paths stay in sync.
    user.value = session?.user ?? null;
    token.value = session?.token ?? null;
    roles.value = session?.roles ?? [];
    return session?.user.emailVerified ?? false;
  }

  return {
    user,
    token,
    error,
    loading,
    isAuthenticated,
    isEmailVerified,
    roles,
    hasPermission,
    onIdentityChanged,
    ready,
    accountPending,
    register,
    accountSetupFinished,
    login,
    logout,
    requestPasswordReset,
    resendVerificationEmail,
    refreshSession,
  };
});
